import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Table, Badge, Spinner, Modal, Row, Col } from 'react-bootstrap';
import { FaMapMarkerAlt, FaCog, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import axios from './utils/axiosConfig';

const SmartCoordinateRepair = () => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 獲取需要修復的資料預覽
  const fetchPreview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/repair-preview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewData(response.data);
      setMessage({ type: 'info', text: `找到 ${response.data.count} 筆需要修復的資料` });
    } catch (error) {
      console.error('獲取預覽失敗:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || '獲取預覽失敗，請稍後再試' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 執行智能修復
  const executeRepair = async () => {
    try {
      setLoading(true);
      setShowConfirmModal(false);
      const token = localStorage.getItem('token');
      
      const response = await axios.post('/smart-coordinate-repair', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRepairResult(response.data);
      setMessage({ 
        type: 'success', 
        text: `修復完成！成功更新 ${response.data.summary.successful_updates} 筆資料` 
      });
      
      // 修復完成後重新獲取預覽
      setTimeout(() => {
        fetchPreview();
      }, 1000);
      
    } catch (error) {
      console.error('修復失敗:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || '修復失敗，請稍後再試' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  // 渲染需要修復的項目
  const renderRepairNeeds = (employee) => {
    const needs = [];
    if (!employee.m_type || employee.m_type === '') needs.push('類型');
    if (!employee.m_lat || employee.m_lat === 0.000000) needs.push('緯度');
    if (!employee.m_lng || employee.m_lng === 0.000000) needs.push('經度');
    
    return needs.map(need => (
      <Badge key={need} bg="warning" className="me-1" style={{ fontSize: '10px' }}>
        {need}
      </Badge>
    ));
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <FaMapMarkerAlt className="me-2" />
                智能座標修復系統
              </h4>
            </Card.Header>
            <Card.Body>
              {/* 說明區域 */}
              <Alert variant="info" className="mb-4">
                <h6><FaCog className="me-2" />功能說明</h6>
                <ul className="mb-0">
                  <li>自動檢測缺失 <code>m_type</code>、<code>m_lat</code>、<code>m_lng</code> 的社友資料</li>
                  <li>使用 <code>e_address1</code> (公司地址) 查詢 Google Maps API 獲取座標</li>
                  <li>自動設定 <code>m_type = 'restaurant'</code></li>
                  <li>保護現有資料，不會覆蓋已有的正確數據</li>
                </ul>
              </Alert>

              {/* 操作按鈕 */}
              <div className="d-flex gap-2 mb-4">
                <Button 
                  variant="outline-primary" 
                  onClick={fetchPreview}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  重新掃描
                </Button>
                
                <Button 
                  variant="warning" 
                  onClick={() => setShowConfirmModal(true)}
                  disabled={loading || !previewData || previewData.count === 0}
                >
                  <FaExclamationTriangle className="me-2" />
                  執行修復 ({previewData?.count || 0} 筆)
                </Button>
              </div>

              {/* 訊息顯示 */}
              {message.text && (
                <Alert variant={message.type} className="mb-4">
                  {message.text}
                </Alert>
              )}

              {/* 修復結果摘要 */}
              {repairResult && (
                <Card className="mb-4 border-success">
                  <Card.Header className="bg-success text-white">
                    <h6 className="mb-0">
                      <FaCheckCircle className="me-2" />
                      修復結果摘要
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <div className="text-center">
                          <h3 className="text-primary">{repairResult.summary.total_found}</h3>
                          <small className="text-muted">總共掃描</small>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center">
                          <h3 className="text-success">{repairResult.summary.successful_updates}</h3>
                          <small className="text-muted">成功修復</small>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center">
                          <h3 className="text-danger">{repairResult.summary.failed_updates}</h3>
                          <small className="text-muted">修復失敗</small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* 預覽資料表格 */}
              {previewData && (
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">需要修復的社友清單 ({previewData.count} 筆)</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {previewData.count > 0 ? (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                          <thead className="bg-light sticky-top">
                            <tr>
                              <th>ID</th>
                              <th>姓名</th>
                              <th>社別</th>
                              <th>地址</th>
                              <th>需要修復</th>
                              <th>目前狀態</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.employees.map(employee => (
                              <tr key={employee.e_id}>
                                <td>{employee.e_id}</td>
                                <td>{employee.e_name}</td>
                                <td>
                                  <Badge bg="primary" style={{ fontSize: '10px' }}>
                                    {employee.store_name}
                                  </Badge>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {employee.e_address1?.substring(0, 30)}
                                    {employee.e_address1?.length > 30 ? '...' : ''}
                                  </small>
                                </td>
                                <td>{renderRepairNeeds(employee)}</td>
                                <td>
                                  <small className="text-muted">
                                    類型: {employee.m_type || '無'}<br/>
                                    座標: {employee.m_lat && employee.m_lng && employee.m_lat !== 0 && employee.m_lng !== 0 
                                      ? `${employee.m_lat.toFixed(4)}, ${employee.m_lng.toFixed(4)}`
                                      : '無'
                                    }
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <FaCheckCircle className="text-success mb-2" size={48} />
                        <h6>所有社友的座標資料都已完整！</h6>
                        <small className="text-muted">目前沒有需要修復的資料</small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* 成功修復的清單 */}
              {repairResult && repairResult.updated_employees.length > 0 && (
                <Card className="mt-4">
                  <Card.Header className="bg-success text-white">
                    <h6 className="mb-0">成功修復的社友 ({repairResult.updated_employees.length} 筆)</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Table hover className="mb-0">
                        <thead className="bg-light sticky-top">
                          <tr>
                            <th>ID</th>
                            <th>姓名</th>
                            <th>地址</th>
                            <th>更新內容</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repairResult.updated_employees.map(employee => (
                            <tr key={employee.e_id}>
                              <td>{employee.e_id}</td>
                              <td>{employee.e_name}</td>
                              <td>
                                <small className="text-muted">
                                  {employee.address?.substring(0, 30)}
                                  {employee.address?.length > 30 ? '...' : ''}
                                </small>
                              </td>
                              <td>
                                {employee.updates.coordinates && (
                                  <Badge bg="info" className="me-1" style={{ fontSize: '10px' }}>
                                    座標: {employee.updates.coordinates}
                                  </Badge>
                                )}
                                {employee.updates.m_type && (
                                  <Badge bg="warning" className="me-1" style={{ fontSize: '10px' }}>
                                    類型: {employee.updates.m_type}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* 修復失敗的清單 */}
              {repairResult && repairResult.error_employees.length > 0 && (
                <Card className="mt-4">
                  <Card.Header className="bg-danger text-white">
                    <h6 className="mb-0">修復失敗的社友 ({repairResult.error_employees.length} 筆)</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Table hover className="mb-0">
                        <thead className="bg-light sticky-top">
                          <tr>
                            <th>ID</th>
                            <th>姓名</th>
                            <th>地址</th>
                            <th>錯誤原因</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repairResult.error_employees.map(employee => (
                            <tr key={employee.e_id}>
                              <td>{employee.e_id}</td>
                              <td>{employee.e_name}</td>
                              <td>
                                <small className="text-muted">
                                  {employee.address?.substring(0, 30)}
                                  {employee.address?.length > 30 ? '...' : ''}
                                </small>
                              </td>
                              <td>
                                <small className="text-danger">
                                  {employee.error}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 確認執行修復的 Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-warning me-2" />
            確認執行修復
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <h6>⚠️ 重要提醒</h6>
            <ul className="mb-0">
              <li>即將修復 <strong>{previewData?.count}</strong> 筆社友的座標資料</li>
              <li>此操作會調用 Google Maps API，可能需要較長時間</li>
              <li>修復過程中請勿關閉頁面</li>
              <li>原本已有正確資料的欄位不會被覆蓋</li>
            </ul>
          </Alert>
          <p>確定要繼續執行嗎？</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            取消
          </Button>
          <Button variant="warning" onClick={executeRepair}>
            <FaExclamationTriangle className="me-2" />
            確定執行修復
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SmartCoordinateRepair;